import {Component, Inject, OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {TranslateService} from "@ngx-translate/core";
import {UntypedFormGroup} from "@angular/forms";
import {SelectItem} from "@mydocma/dynamic-form";
import {EntityScopeEnum} from "@docma-mm-core/entity/entity-scope.enum";
import {Layout} from "@docma-mm-core/layout/layout";
import {MmProjectPermissionsService} from "../../../../mm-project/mm-project-permissions-service";
import {ProjectActionPermissionEnum} from "@docma-mm-core/permission/project-action-permission.enum";
import {ActionPermissionEnum} from "@docma-mm-core/permission/action-permission.enum";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {BehaviorSubject, combineLatest} from "rxjs";
import {LayoutApiService} from "@docma-mm-core/layout/layout-api.service";
import { User } from '@docma-mm-core/user/user';
import { Project } from '@docma-mm-core/project/project';
import {MatSelectChange} from "@angular/material/select";
import {MediaQueryService} from "@mydocma/core";
import {LayoutEditService} from "../layout-edit-service";
import {NotificationService} from "@mydocma/component-library/src/lib/notification";
import {PromptDialogService} from "@mydocma/component-library/src/lib/prompt-dialog";
import {getDuplicateName} from "@docma-mm-core/utils/utils";

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-layout-duplicate-dialog',
  templateUrl: './layout-duplicate-dialog.component.html',
  styleUrls: ['./layout-duplicate-dialog.component.scss']
})
export class LayoutDuplicateDialogComponent implements OnInit {

  layoutDuplicateModes = LayoutDuplicateMode;
  formGroup: UntypedFormGroup;
  mobileMode: boolean;

  duplicateMode: LayoutDuplicateMode;
  scopes: SelectItem[];
  selectedScope: SelectItem;
  isScopeForcedSubject = new BehaviorSubject<boolean>(false);
  isScopeForced$ = this.isScopeForcedSubject.asObservable();

  loadedLayout: Layout;
  loadingSubject = new BehaviorSubject<Boolean>(true);
  loading$ = this.loadingSubject.asObservable();
  savingSubject = new BehaviorSubject<Boolean>(false);
  saving$ = this.savingSubject.asObservable();

  titleSubject = new BehaviorSubject<string>(this.translateService.instant("MM.layout.layoutDuplicateMessage.title").replace(' - {{ value }}', ''));
  title$ = this.titleSubject.asObservable();

  currentNameSuffix: string;


  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: {layoutId: number, project: Project, user: User, service: LayoutEditService, nameSuffix: string, layoutNames: { [scope: number]: string[] }},
              private dialogRef: MatDialogRef<LayoutDuplicateDialogComponent>,
              private readonly translateService: TranslateService,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly layoutApiService: LayoutApiService,
              public readonly mediaQueryService: MediaQueryService,
              private readonly notificationService: NotificationService,
              private readonly dialogService: PromptDialogService) {

    this.initialize();
  }

  ngOnInit(): void {
  }

  onResize(event) {
    this.mobileMode = event.contentRect.width <= 600;
  }

  async onSaveChanges() {
    this.savingSubject.next(true);

    try {
      this.loadedLayout.Name = this.loadedLayout.Name + this.currentNameSuffix;

      if (+this.loadedLayout.Scope != this.selectedScope.id) {
        if (this.selectedScope.id === +EntityScopeEnum.Global) {
          this.loadedLayout.Project = null;
          this.loadedLayout.User = null;
          this.loadedLayout.Scope = EntityScopeEnum.Global;
        } else if (this.selectedScope.id === +EntityScopeEnum.Project) {
          this.loadedLayout.Project = new Project({Id: this.dialogData.project.Id});
          this.loadedLayout.User = null;
          this.loadedLayout.Scope = EntityScopeEnum.Project;
        } else if (this.selectedScope.id === +EntityScopeEnum.User) {
          this.loadedLayout.Project = null;
          this.loadedLayout.User = new User({Id: this.dialogData.user.Id});
          this.loadedLayout.Scope = EntityScopeEnum.User;
          this.loadedLayout.IsFavoriteAll = false;
        }
      }

      this.removeInvalidFilters();

      const savedLayout: Layout = await this.layoutApiService.createLayout(this.loadedLayout).toPromise()
      this.dialogRef.close({Layout: savedLayout});
    } catch (err) {
      this.notificationService.error(this.translateService.instant('MM.layout.errorSavingLayout'))
    }
    finally {
      this.savingSubject.next(false);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  initialize() {
    this.loadingSubject.next(true);

    this.currentNameSuffix = this.dialogData.nameSuffix;

    let hasEditProjectLayout = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditProjectLayout]);
    let hasEditGlobalLayout = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditGlobalLayout]);

    let test = combineLatest([hasEditGlobalLayout, hasEditProjectLayout, this.layoutApiService.getFullLayout(this.dialogData.layoutId)])
      .pipe(untilDestroyed(this))
      .subscribe(([editGlobalPerm, editProjectPerm, loadedLayout]) => {
        this.loadedLayout = loadedLayout;

        this.titleSubject.next(this.translateService.instant("MM.layout.layoutDuplicateMessage.title", {value: this.loadedLayout.Name}));

        const source = Object.keys(EntityScopeEnum)
          .filter(key =>  !isNaN(Number(EntityScopeEnum[key]))
            && (EntityScopeEnum[key] === EntityScopeEnum.Global && editGlobalPerm
              || EntityScopeEnum[key] === EntityScopeEnum.Project && editProjectPerm
              || EntityScopeEnum[key] === EntityScopeEnum.User)
          )
          .map(key => ({
            id: EntityScopeEnum[key],
            text: this.translateService.instant('MM.layout.details.type' + key)
          }));

        this.scopes = source;

        this.selectedScope = this.scopes.find(x => x.id === +loadedLayout.Scope);

        if (!this.selectedScope) {
          this.isScopeForcedSubject.next(true);
          this.selectedScope = this.scopes.find(x => x.id === +EntityScopeEnum.User);
          this.duplicateMode = LayoutDuplicateMode.DuplicateAndChangeType;
        }
        else {
          this.duplicateMode = LayoutDuplicateMode.OnlyDuplicate;
        }

        loadedLayout.Id = undefined;
        loadedLayout.SUid = undefined;


        if (this.duplicateMode === LayoutDuplicateMode.OnlyDuplicate) {
          // console.log('LayoutDuplicateMode.OnlyDuplicate');
        }
        else if (this.duplicateMode === LayoutDuplicateMode.DuplicateAndChangeType) {
          // console.log('LayoutDuplicateMode.DuplicateAndChangeType');
          // const hasInvalidFilters = this.validateInvalidFilters(loadedLayout);

          if (+loadedLayout.Scope != this.selectedScope.id) {
            loadedLayout.Scope = this.selectedScope.id;

            if (loadedLayout.Scope === EntityScopeEnum.Global) {
              loadedLayout.User = null;
              loadedLayout.Project = null;
            }
            else if (loadedLayout.Scope === EntityScopeEnum.Project) {
              loadedLayout.User = null;
              loadedLayout.Project = this.dialogData.project;
            }
            else if (loadedLayout.Scope === EntityScopeEnum.User) {
              loadedLayout.User = this.dialogData.user;
              loadedLayout.Project = null;
              loadedLayout.IsFavoriteAll = false;
            }
          }
        }
        this.loadingSubject.next(false);
      });
  }

  hasInvalidFilters(layout: Layout): boolean {
    let hasInvalidFilters: boolean;

    if (this.selectedScope.id === +EntityScopeEnum.Global) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.Project != null || x.Filter.User != null);
    }
    else if (this.selectedScope.id === +EntityScopeEnum.Project) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.User != null);
    }
    else if (this.selectedScope.id === +EntityScopeEnum.User) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.Project != null);
    }
    return hasInvalidFilters;
  }

  removeInvalidFilters() {

    if (this.selectedScope.id === +EntityScopeEnum.Global) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.Project != null || f.Filter.User != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
    else if (this.selectedScope.id === +EntityScopeEnum.Project) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.User != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
    else if (this.selectedScope.id === +EntityScopeEnum.User) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.Project != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
  }

  selectedScopeChanged($event: MatSelectChange) {
    if (this.hasInvalidFilters(this.loadedLayout)) {
      this.dialogService.showNotificationDialog({ title: this.translateService.instant('MM.layout.messageChangeLayoutTypeTitle'), content: this.translateService.instant('MM.layout.messageChangeLayoutType')})
    }
    const scopeSwitched = (+this.loadedLayout.Scope !== this.selectedScope.id);
    this.currentNameSuffix = getDuplicateName(this.loadedLayout.Name, this.dialogData.layoutNames[this.selectedScope.id], this.translateService);
    // this.titleSubject.next(this.translateService.instant("MM.layout.layoutDuplicateMessage.title").replace('###layoutName###', `${this.loadedLayout.Name}${this.currentNameSuffix}`));
  }

  onDuplicateModeChanged() {
    if (this.duplicateMode === LayoutDuplicateMode.OnlyDuplicate) {
      this.currentNameSuffix = getDuplicateName(this.loadedLayout.Name, this.dialogData.layoutNames[+EntityScopeEnum.User], this.translateService);
      // this.titleSubject.next(this.translateService.instant("MM.layout.layoutDuplicateMessage.title").replace('###layoutName###', `${this.loadedLayout.Name}${this.currentNameSuffix}`));
    }
  }
}

export enum LayoutDuplicateMode {
  OnlyDuplicate = 0,
  DuplicateAndChangeType = 1
}
